import jsPDF from 'jspdf';
import logo from '../images/logo.png';
import { CIL_POINTS_LABEL } from './cilPoints';
import { createPdfLayoutCompiler } from './pdfLayoutCompiler';

async function getBase64ImageFromURL(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function formatReceiptDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('it-IT', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function formatPaymentMethod(method) {
  if (method === 'punti') return CIL_POINTS_LABEL;
  if (method === 'elettronico') return 'elettronico';
  return method || '';
}

function formatEuro(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return 'Gratuito';
  return `${n.toFixed(2)} €`;
}

/**
 * "Evento - Evento - Settimana 1" → "Evento - Settimana 1"
 */
function formatReceiptProductName(name, eventName = '') {
  const raw = String(name || '').trim();
  if (!raw) return 'Voce';

  const parts = raw
    .split(/\s+-\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  const collapsed = parts.reduce((acc, part) => {
    if (!acc.length || acc[acc.length - 1] !== part) {
      acc.push(part);
    }
    return acc;
  }, []);

  const event = String(eventName || '').trim();
  if (event) {
    const rest = collapsed.filter((part) => part !== event);
    if (rest.length) {
      return `${event} - ${rest.join(' - ')}`;
    }
    return event;
  }

  return collapsed.join(' - ') || raw;
}

function formatLineLabel(line, eventName = '') {
  const cleanName = formatReceiptProductName(line.name || 'Voce', eventName);
  if (line.payments?.length === 1 && line.payments[0].paymentType) {
    return `${cleanName} (${line.payments[0].paymentType})`;
  }
  if (line.payments?.length > 1) {
    const breakdown = line.payments
      .map(
        (p) =>
          `${p.paymentType || 'pagamento'} ${Number(p.amount || 0).toFixed(2)}€`
      )
      .join(' + ');
    return `${cleanName} (${breakdown})`;
  }
  if (line.displayLabel) {
    const suffixMatch = String(line.displayLabel).match(/(\s*\([^)]*\))\s*$/);
    return `${cleanName}${suffixMatch ? suffixMatch[1] : ''}`;
  }
  return cleanName;
}

/**
 * Genera e scarica la ricevuta PDF da un receiptPayload consolidato.
 * @param {object} payload
 */
export async function downloadOrderReceiptPdf(payload) {
  if (!payload) return;

  let logoData = '';
  try {
    logoData = await getBase64ImageFromURL(logo);
  } catch (error) {
    console.error('Errore nel caricamento del logo:', error);
  }

  // eslint-disable-next-line new-cap
  const doc = new jsPDF('p', 'pt', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  const lineHeight = 20;

  const giallo = '#ffcc00';
  const blu = '#36a9e1';
  const violaScuro = '#3b235a';

  const anagrafica = payload.anagrafica || {};
  const lines = Array.isArray(payload.lines) ? payload.lines : [];
  const eventTitle =
    payload.eventName || `Tesseramento ${new Date().getFullYear()}`;
  const eventLabel = payload.eventName ? 'Evento' : 'Prodotto';

  doc.setFillColor(giallo);
  doc.rect(0, 0, pageWidth, 135, 'F');
  if (logoData) {
    doc.addImage(logoData, 'PNG', pageWidth - margin - 100, 20, 100, 35);
  }
  doc.setFontSize(11);
  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.text('CIL – Creatività in Libertà APS', margin, 30);
  doc.setFont('helvetica', 'normal');
  doc.text('CF/P.IVA: 94090260012', margin, 45);
  doc.text('Sede Legale: Via GB Buschetti 18, 10023 Chieri (TO)', margin, 60);
  doc.text(
    'Sede Operativa: Via Carlo Alberto 7, 10023 Chieri (TO)',
    margin,
    75
  );
  doc.text('Email: info@cilassociazione.it', margin, 90);
  doc.text('Telefono: +39 353 468 2493', margin, 105);
  doc.text('www.cilassociazione.it', margin, 120);

  const compiler = createPdfLayoutCompiler(doc, {
    margin,
    lineHeight,
    footerHeight: 50,
    startY: 135 + 20,
  });

  compiler.addHorizontalRule(blu, { before: 0, after: 30 });

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(violaScuro);
  compiler.ensureSpace(lineHeight + 10);
  doc.text('Ricevuta di Pagamento', pageWidth / 2, compiler.getY(), {
    align: 'center',
  });
  compiler.advance(40);

  const socioRows = [
    ['Nome Socio', `${anagrafica.nomeSocio || ''}`.trim()],
    ['Cognome Socio', `${anagrafica.cognomeSocio || ''}`.trim()],
    ['Email', anagrafica.email || ''],
    ['Codice Fiscale', anagrafica.codiceFiscaleSocio || ''],
    ['N° Tessera', anagrafica.Numero_Tessera || ''],
    [
      'Indirizzo',
      [
        anagrafica.indirizzoVia,
        anagrafica.indirizzoCap,
        anagrafica.indirizzoCitta,
      ]
        .filter(Boolean)
        .join(', '),
    ],
    ['Ricevuta N:', payload.receiptNumber || ''],
    ['Data', formatReceiptDate(payload.orderDate)],
    [eventLabel, eventTitle],
    ['Metodo', formatPaymentMethod(payload.paymentMethod)],
    [
      'Stato',
      payload.paymentStatusLabel ||
        (payload.paymentStatus === 'succeeded'
          ? 'Pagamento Effettuato'
          : payload.paymentStatus || ''),
    ],
    ['Totale', formatEuro(payload.total)],
  ];

  const parentRows =
    anagrafica.nomeGenitore ||
    anagrafica.cognomeGenitore ||
    anagrafica.codiceFiscaleGenitore
      ? [
          ['Nome Genitore', `${anagrafica.nomeGenitore || ''}`.trim()],
          ['Cognome Genitore', `${anagrafica.cognomeGenitore || ''}`.trim()],
          ['Codice Fiscale Genitore', anagrafica.codiceFiscaleGenitore || ''],
        ]
      : [];

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);

  socioRows.forEach(([label, value]) => {
    compiler.addKeyValueRow(label, value);
  });

  if (parentRows.length) {
    compiler.advance(10);
    compiler.addSectionTitle('Dati Genitore');
    parentRows.forEach(([label, value]) => {
      compiler.addKeyValueRow(label, value);
    });
  }

  if (lines.length) {
    compiler.advance(20);
    compiler.addSectionTitle('Prodotti Acquistati');

    lines.forEach((line) => {
      const label = formatLineLabel(line, eventTitle);
      const price = `${Number(line.amount || 0).toFixed(2)} €`;
      compiler.addBulletLine(`• ${label}: ${price}`);
    });
  }

  compiler.drawFooter(() => {
    doc.setFillColor(violaScuro);
    doc.rect(0, compiler.pageHeight - 50, pageWidth, 50, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#ffffff');
    doc.text(
      'Grazie per aver scelto CIL – Creatività in Libertà APS',
      pageWidth / 2,
      compiler.pageHeight - 25,
      { align: 'center' }
    );
  });

  doc.save(`ricevuta_ordine_${payload.receiptNumber || 'ordine'}.pdf`);
}

export default downloadOrderReceiptPdf;
